import { Outlet, useLocation, useParams } from "react-router-dom";
import AdminLayout from "../../layout/AdminLayout";
import useApiQuery from "../../Api/useApiQuery";
import React, { useContext } from "react";
import { useCurrentUserRole } from "../../hooks/useCurrentUser";
import { useSavV2Counts } from "../../Api/sav-v2/useSavV2Counts";
import { useSavFilterStore } from "../../Stores/SavFilterStore";
import ManagerFilterDropdown from "./components/ManagerFilterDropdown";
import { NewsArrival } from "@/features/news/components/reader/NewsArrival";
import { useNewsUnreadCount } from "@/features/news/hooks/useNewsReader";
import { NEWS_FEED_PATH } from "@/features/news/constants/news.constants";


const CountContext = React.createContext<{
  refetchCounts: () => Promise<any>;
  refetchTasksCount: () => Promise<any>;
}>({
  refetchCounts: async () => void 0,
  refetchTasksCount: async () => void 0,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useCountRefetchContext = () => useContext(CountContext);


const now = new Date().toISOString();
export default function Layout() {

  const { isAdminOrSupervisor } = useCurrentUserRole();
  const location = useLocation();
  const params = useParams();
  const { savId } = params;

  // Récupérer le filtre manager depuis le store
  const { getManagerFilterParam } = useSavFilterStore();
  const managerFilter = getManagerFilterParam();

  // Counts pour les listes V1 uniquement
  const { data: countsV1, refetch: refetchCountsV1 } = useApiQuery<{
    all: number;
    new: number;
    waiting_client: number;
    waiting_operator: number;
    waiting_guarantor: number;
    sans_suite: number;
    litige: number;
    a_prendre_en_charge: number;
    pris_en_charge: number;
    new_beneficiary_messages: number;
  }>("sav/counts", {
    params: {
      ...(managerFilter && { managerFilter }),
      savVersion: 1, // Uniquement SAV v1
    },
  });

  // Counts pour les listes V1 + V2
  const { data: countsAll, refetch: refetchCountsAll } = useApiQuery<{
    all: number;
    sans_suite: number;
    litige: number;
    pris_en_charge: number;
  }>("sav/counts", {
    params: {
      ...(managerFilter && { managerFilter }),
      // Pas de savVersion = v1 ET v2
    },
  });

  const { data: countTasks, refetch: refetchTasksCount } = useApiQuery<any>("tasks/count", {
    enabled: !!savId,
    params: {
      savId,
      isDone: false,
      beforeDateTime: now,
    }
  });

  const { data: countOverdueTasks } = useApiQuery<any>("tasks/count", {
    params: {
      isDone: false,
      beforeDateTime: now,
    }
  });

  // Récupérer les counts pour les filtres SAV v2 avec le filtre manager
  const { counts: savV2Counts } = useSavV2Counts(managerFilter);

  // Nouveautés non lues (fil d'annonces produit) — 0 tant que le nombre n'est
  // pas connu ou si l'appel échoue : pas de badge dans ce cas.
  const newsUnreadCount = useNewsUnreadCount("SAV");

  return (
    <CountContext.Provider
      value={{
        refetchCounts: async () => {
          await refetchCountsV1();
          await refetchCountsAll();
        },
        refetchTasksCount
      }}
    >
      <AdminLayout
        SidebarHeader={<ManagerFilterDropdown />}
        SidebarLinkSections={[
          {
            label: "GLOBAL",
            links: [
              {
                label: "Tous les SAV (V1 / V2)",
                to: "/all",
                badge: countsAll?.all ? countsAll.all.toString() : undefined,
              },
              {
                label: "Tous les SAV (liste v2)",
                to: "/savs-list-unified",
              },
              {
                label: "Liste des contrats / bénéficiaires",
                to: "/contracts-list",
              },
              {
                label: "Tâches",
                to: "/tasks/calendar",
                badge: countOverdueTasks?.count ? countOverdueTasks.count.toString() : undefined,
                badgeClassName: "bg-danger",
              },
              {
                label: "Nouveautés",
                to: NEWS_FEED_PATH,
                badge: newsUnreadCount ? newsUnreadCount.toString() : undefined,
              },
            ]
          },
          {
            label: "Nouveaux SAV",
            links: [
              {
                label: "Nouveaux (V1)",
                to: "/status/2?savVersion=1",
                badge: countsV1?.new ? countsV1.new.toString() : undefined,
              },
              {
                label: "SAV non assignés (V2)",
                to: "/savs-v2/filter/unassigned",
                badge: savV2Counts?.unassigned ? savV2Counts.unassigned.toString() : undefined,
              },
            ]
          },
          {
            label: "Gestionnaire",
            links: [
              {
                label: "En attente gestionnaire (V1)",
                to: "/status/4?savVersion=1",
                badge: countsV1?.waiting_operator ? countsV1.waiting_operator.toString() : undefined,
              },
              {
                label: "Rechercher réparateur (V2)",
                to: "/savs-v2/filter/SEARCHING_REPAIRER",
                badge: savV2Counts?.searchingRepairer ? savV2Counts.searchingRepairer.toString() : undefined,
              },
              {
                label: "Transcription devis (V2)",
                to: "/savs-v2/filter/WAITING_QUOTE_TRANSCRIPTION",
                badge: savV2Counts?.transcribingQuote ? savV2Counts.transcribingQuote.toString() : undefined,
              },
              {
                label: "Analyse de la panne (V2)",
                to: "/savs-v2/filter/ANALYZING_QUOTE",
                badge: savV2Counts?.analyzingQuote ? savV2Counts.analyzingQuote.toString() : undefined,
              },
              {
                label: "Formalisation décision (V2)",
                to: "/savs-v2/filter/FORMALIZING_DECISION",
                badge: savV2Counts?.formalizingDecision ? savV2Counts.formalizingDecision.toString() : undefined,
              },
              {
                label: "Non approuvé garant (V2)",
                to: "/savs-v2/filter/GARANT_REFUSED",
                badge: savV2Counts?.refusedGarant ? savV2Counts.refusedGarant.toString() : undefined,
              },
              {
                label: "Nouveaux messages bénéficiaires V1",
                to: "/all?tagKeys=message-beneficiaire-a-lire-sans-suite,new_beneficiary_action&savVersion=1",
                badge: countsV1?.new_beneficiary_messages ? countsV1.new_beneficiary_messages.toString() : undefined,
                badgeClassName: countsV1?.new_beneficiary_messages ? "bg-danger" : undefined,
              },
              {
                label: "Nouveaux messages bénéficiaires V2",
                to: "/savs-v2/filter/NEW_BENEFICIARY_MESSAGES",
                badge: savV2Counts?.newBeneficiaryMessages ? savV2Counts.newBeneficiaryMessages.toString() : undefined,
                badgeClassName: savV2Counts?.newBeneficiaryMessages ? "bg-danger" : undefined,
              },
              {
                label: "Messages internes notifiés (V2)",
                to: "/savs-v2/filter/NEW_INTERNAL_MESSAGES",
                badge: savV2Counts?.newInternalMessages ? savV2Counts.newInternalMessages.toString() : undefined,
                badgeClassName: savV2Counts?.newInternalMessages ? "bg-warning" : undefined,
              },
              {
                label: "Messages garant (V2)",
                to: "/savs-v2/filter/NEW_GARANT_MESSAGES",
                badge: savV2Counts?.newGarantMessages ? savV2Counts.newGarantMessages.toString() : undefined,
                badgeClassName: savV2Counts?.newGarantMessages ? "bg-warning" : undefined,
              },
            ]
          },
          {
            label: "Bénéficiaire",
            links: [
              {
                label: "En attente bénéficiaire (V1)",
                to: "/status/3?savVersion=1",
                badge: countsV1?.waiting_client ? countsV1.waiting_client.toString() : undefined,
              },
              {
                label: "En attente devis (V2)",
                to: "/savs-v2/filter/WAITING_QUOTE",
                badge: savV2Counts?.waitingQuoteBeneficiary ? savV2Counts.waitingQuoteBeneficiary.toString() : undefined,
              },
              {
                label: "En attente signature bénéficiaire (V2)",
                to: "/savs-v2/filter/WAITING_BENEFICIARY_SIGNATURE",
                badge: savV2Counts?.waitingBeneficiarySignature ? savV2Counts.waitingBeneficiarySignature.toString() : undefined,
              },
            ]
          },
          {
            label: "Garant",
            links: [
              {
                label: "En attente garant (V1)",
                to: "/status/6?savVersion=1",
                badge: countsV1?.waiting_guarantor ? countsV1.waiting_guarantor.toString() : undefined,
              },
              {
                label: "En attente prise en charge directe (V2)",
                to: "/savs-v2/filter/DIRECT_COVERAGE",
                badge: savV2Counts?.directCoverage ? savV2Counts.directCoverage.toString() : undefined,
              },
              {
                label: "En attente de signature garant (V2)",
                to: "/savs-v2/filter/WAITING_GARANT_SIGNATURE",
                badge: savV2Counts?.waitingGarantSignature ? savV2Counts.waitingGarantSignature.toString() : undefined,
              },
            ]
          },
          {
            // Cycle paiement réparateur (cf SPECS/repairer_payment Phases 5-6).
            // Regroupé et replié par défaut : concerne uniquement les garants
            // avec le workflow activé, à ne pas polluer la sidebar des autres.
            label: "Cycle paiement réparateur",
            defaultCollapsed: true,
            links: [
              {
                label: "En attente facture réparateur (V2)",
                to: "/savs-v2/filter/WAITING_REPAIRER_INVOICE_UPLOAD",
                badge: savV2Counts?.waitingRepairerInvoiceUpload ? savV2Counts.waitingRepairerInvoiceUpload.toString() : undefined,
              },
              {
                label: "En attente paiement vendeur (V2)",
                to: "/savs-v2/filter/WAITING_GARANT_PAYMENT",
                badge: savV2Counts?.waitingGarantPayment ? savV2Counts.waitingGarantPayment.toString() : undefined,
              },
              {
                // Bloc 5.4 : escalades sidebar, pas d'emails internes.
                label: "Paiement vendeur en retard (V2)",
                to: "/savs-v2/filter/PAYMENT_OVERDUE_GARANT",
                badge: savV2Counts?.paymentOverdueGarant
                  ? savV2Counts.paymentOverdueGarant.toString()
                  : undefined,
              },
              {
                label: "Facture réparateur en retard (V2)",
                to: "/savs-v2/filter/REPAIRER_UPLOAD_OVERDUE",
                badge: savV2Counts?.repairerUploadOverdue
                  ? savV2Counts.repairerUploadOverdue.toString()
                  : undefined,
              },
              {
                label: "Dispute paiement réparateur (V2)",
                to: "/savs-v2/filter/PAYMENT_DISPUTE_TO_TREAT",
                badge: savV2Counts?.paymentDisputeToTreat
                  ? savV2Counts.paymentDisputeToTreat.toString()
                  : undefined,
              },
              {
                label: "Réparateur sans email (V2)",
                to: "/savs-v2/filter/REPAIRER_NO_EMAIL_ACTION_REQUIRED",
                badge: savV2Counts?.repairerNoEmailActionRequired
                  ? savV2Counts.repairerNoEmailActionRequired.toString()
                  : undefined,
              },
            ]
          },
          {
            label: "Terminé",
            links: [
              {
                label: "Pris en charge (V1 / V2)",
                to: "/status/8",
                badge: countsAll?.pris_en_charge ? countsAll.pris_en_charge.toString() : undefined,
              },
              {
                label: "Sans suite (V1 / V2)",
                to: "/status/9",
                badge: countsAll?.sans_suite ? countsAll.sans_suite.toString() : undefined,
              },
            ]
          },
          {
            label: "Litige",
            links: [
              {
                label: "Litige (V1 / V2)",
                to: "/status/5",
                badge: countsAll?.litige ? countsAll.litige.toString() : undefined,
              },
            ]
          },
          ...(!location.pathname.startsWith("/sav/") ? [] : [{
            label: "Sav " + params.savId,
            links: [
              {
                label: "Informations",
                to: "/sav/" + params.savId,
              },
              {
                label: "Taches",
                to: `/sav/${params.savId}/tasks-calendar`,
                badge: countTasks?.count ? countTasks.count.toString() : undefined,
                badgeClassName: "bg-danger",
              },
              {
                label: "Documents internes",
                to: `/sav/${params.savId}/internal-documents`,
              },
              {
                label: "Documents partagés",
                to: `/sav/${params.savId}/documents`,
              },
              {
                label: "Prise en charge - Reparateur",
                to: `/sav/${params.savId}/reparateur`,
              },
              {
                label: "Conversation IA",
                to: `/sav/${params.savId}/ia`,
              },
              {
                label: "Messagerie beneficiaire",
                to: `/sav/${params.savId}/beneficiary-messages`,
              },
              {
                label: "Messagerie interne",
                to: `/sav/${params.savId}/internal-messages`,
              },
              {
                label: "Historique des actions",
                to: `/sav/${params.savId}/actions-logs`,

              }
            ],
          }]),

          ...(isAdminOrSupervisor ? [{
            label: "Paramètres",
            links: [
              {
                label: "Tags",
                to: "/settings/tags",
              }
            ]
          }] : [])
        ]}
      >
        {/* Nouveautés : bandeau (en tête du contenu) + modale d'arrivée, puis la page. */}
        <NewsArrival platform="SAV" />
        <Outlet />
      </AdminLayout>
    </CountContext.Provider>
  )
}